import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppLayoutContextConsumer } from '@occc-app/design-system';
import { useGetCampaignName, getCreatePageLinks } from '@occc-app/routes/CampaignComponents/utils';
import { Offer_Engagement_Type } from '@occc/graphql';
import { OfferType } from '@occc-app/offerValidationService/types';

import { OfferCreateConfigurePage } from '@occc-app/routes/Offer/OfferCreatePage/OfferCreateConfigurePage';
import { useChainId } from '@occc-app/Chain/hooks';

import { Flow_Types } from '@occc-app/offerHelpers/types';
import { useMakeOffersFilter } from '@occc-app/offerHelpers/hooks/use-make-offers-filter.hook';
import { useNavigateToCampaign } from '@occc-app/offerHelpers/hooks/useNavigateToCampaign';
import { CampaignComponentTabs } from '@occc-app/routes/CampaignComponents/components/ConfigurationLayout';
import { ChallengeConfigureFormInitialValues, ChallengeConfigureFormValues } from '../types';
import { ConfigureForm } from '../components/ChallengeConfigureForm/ConfigureForm';
import { getConfigureFormOperations } from '../components/ChallengeConfigureForm/utils/configureFormOperations';
import { transformOfferToInitialValues } from '../components/ChallengeConfigureForm/utils/configureFormInitialValues';
import { messages } from '../messages';
import { routeCreator } from '../utils';

export const ChallengeCreatePageContainer = () => {
  const navigate = useNavigate();
  const { id: campaignId = '' } = useParams<{ id: string }>();

  const chainId = useChainId();
  const campaignName = useGetCampaignName(chainId, campaignId);
  const navigateToCampaign = useNavigateToCampaign(campaignId);

  const links = useMemo(
    () => getCreatePageLinks(campaignId, campaignName, 'New Challenge'),
    [campaignId, campaignName],
  );
  useAppLayoutContextConsumer(links);

  const makeOffersFilter = useMakeOffersFilter();

  return (
    <OfferCreateConfigurePage<ChallengeConfigureFormValues, ChallengeConfigureFormInitialValues>
      pageTitle='Campaign > Create'
      subtitle={messages.subtitle}
      offerType={OfferType.Challenge}
      engagementType={Offer_Engagement_Type.Transactional}
      campaignId={campaignId}
      getConfigureOperations={getConfigureFormOperations}
      getInitialValues={transformOfferToInitialValues}
      onOfferCreated={(offerId) =>
        navigate(routeCreator.configureWithTab(campaignId, offerId, CampaignComponentTabs.Target))
      }
      renderConfigureForm={({ onSubmit, initialValues, loading }) => (
        <ConfigureForm
          onSubmit={onSubmit}
          initialValues={initialValues}
          loading={loading}
          onCancelClick={navigateToCampaign}
          makeOffersFilter={makeOffersFilter}
          currentFlowType={Flow_Types.Configure}
          isFormEditable
        />
      )}
    />
  );
};
